import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { clearAuthStorageOnSignOut } from '@/integrations/supabase/rememberMeStorage';
import { useBatchStore } from '@/features/conciliation/store/batchStore';
import { useConciliationStore } from '@/features/conciliation/store/conciliationStore';
import { setActiveBusinessId, resetActiveBusinessId } from '@/services/authState';

// =============================================================================
// TYPES (matching Edge Function response)
// =============================================================================

export type AppRole = 'firm_owner' | 'admin' | 'attorney' | 'paralegal' | 'support';
export type OrgRole = 'owner' | 'admin' | 'member';
export type PlatformRole = 'super_admin';

export interface OrganizationMembership {
  orgId: string;
  orgName: string;
  orgCode: string;
  role: OrgRole | null; // null for business members who aren't org owners
  singleBusinessMode: boolean;
}

export interface BusinessMembership {
  businessId: string;
  businessName: string;
  businessCode: string;
  orgId: string;
  role: AppRole;
  isActive: boolean;
}

export interface ExtendedAuthContext {
  userId: string;
  email: string;
  fullName: string | null;
  displayName: string | null;
  platformRole: PlatformRole | null;
  organizations: OrganizationMembership[];
  businessMemberships: BusinessMembership[];
  /** The org_id from the JWT — the authoritative active org for this session */
  activeOrgId: string | null;
  activeBusinessId: string | null;
  activeBusiness: BusinessMembership | null;
  clientType: 'organization' | 'business' | null;
  hasPendingInvite: boolean;
  singleBusinessMode: boolean;
  /** True when multi-org user has no valid stored active business — must select a firm */
  requiresFirmSelection: boolean;
}

export interface AuthContextValue {
  // Core auth state
  user: User | null;
  session: Session | null;
  loading: boolean;
  initializing: boolean;
  contextReady: boolean;
  contextError: string | null;
  signOut: () => Promise<void>;
  isAuthenticated: boolean;
  
  // Extended RBAC context
  platformRole: PlatformRole | null;
  organizations: OrganizationMembership[];
  businessMemberships: BusinessMembership[];
  activeBusinessId: string | null;
  activeBusiness: BusinessMembership | null;
  clientType: 'organization' | 'business' | null;
  hasPendingInvite: boolean;
  singleBusinessMode: boolean;
  
  // Actions
  switchBusiness: (businessId: string) => Promise<boolean>;
  selectOrganization: (orgId: string) => Promise<boolean>;
  refreshAuth: () => Promise<void>;

  // Helper booleans
  isSuperAdmin: boolean;
  /** True when activeOrg.role is 'owner' — use for owner-only management actions */
  isOrgOwner: boolean;
  /** True when activeOrg.role is 'owner' or 'admin' — use for elevated data access checks */
  isOrgAdmin: boolean;
  /** True when user has 2+ org memberships and needs to choose a firm */
  needsFirmSelection: boolean;

  // Active org context (derived from active business)
  activeOrg: OrganizationMembership | null;

  // Legacy fields for backward compatibility
  orgId: string | null;
  orgName: string | null;
  orgCode: string | null;
  role: AppRole | null;
  fullName: string | null;
  /** Per-org display name (falls back to fullName) */
  displayName: string | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// =============================================================================
// CONSTANTS - Industry-standard rate limiting & debounce
// =============================================================================

const MIN_FETCH_INTERVAL_MS = 3000; // 3 seconds minimum between fetches
const RATE_LIMIT_COOLDOWN_MS = 60000; // 60 second cooldown on 429
const MAX_AUTH_FAILURES = 3;
const AUTH_DEBOUNCE_MS = 150; // Debounce auth state changes

// =============================================================================
// PROVIDER
// =============================================================================

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [authContext, setAuthContext] = useState<ExtendedAuthContext | null>(null);
  const [contextLoading, setContextLoading] = useState(false);
  const [contextError, setContextError] = useState<string | null>(null);
  const [authFailureCount, setAuthFailureCount] = useState(0);
  
  // Guards to prevent re-entrant refresh loops
  const isFetchingRef = useRef(false);
  const lastFetchTimeRef = useRef<number>(0);
  const rateLimitedUntilRef = useRef<number>(0);
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasInitializedRef = useRef(false);
  
  // Track initial load and previous session for event-aware loading
  const isInitialLoadRef = useRef(true);
  const prevSessionIdRef = useRef<string | null>(null);
  const isPageVisibleRef = useRef(true);

  // Track page visibility to prevent refetches when tab is in background
  useEffect(() => {
    const handleVisibilityChange = () => {
      isPageVisibleRef.current = !document.hidden;
      if (process.env.NODE_ENV === 'development') {
        console.info('PAGE_VISIBILITY_CHANGED', { isVisible: isPageVisibleRef.current });
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      isPageVisibleRef.current = true; // Reset on unmount
    };
  }, []);

  /**
   * Fetch auth context from edge function.
   * NO token refresh logic here - just fetch. This prevents the infinite loop.
   */
  const fetchAuthContext = useCallback(async () => {
    const now = Date.now();
    
    // Guard: Rate limited
    if (now < rateLimitedUntilRef.current) {
      if (process.env.NODE_ENV === 'development') {
        console.info('AUTH_FETCH_SKIPPED', { 
          reason: 'rate_limited', 
          retryInMs: rateLimitedUntilRef.current - now 
        });
      }
      return;
    }
    
    // Guard: Too soon since last fetch
    if (now - lastFetchTimeRef.current < MIN_FETCH_INTERVAL_MS) {
      if (process.env.NODE_ENV === 'development') {
        console.info('AUTH_FETCH_SKIPPED', { 
          reason: 'too_soon',
          lastFetchMs: now - lastFetchTimeRef.current
        });
      }
      return;
    }
    
    // Guard: Already fetching
    if (isFetchingRef.current) {
      if (process.env.NODE_ENV === 'development') {
        console.info('AUTH_FETCH_SKIPPED', { reason: 'already_fetching' });
      }
      return;
    }
    
    isFetchingRef.current = true;
    lastFetchTimeRef.current = now;
    // Only show loading state on initial load, not on background refreshes
    if (isInitialLoadRef.current) {
      setContextLoading(true);
    }
    setContextError(null);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setContextError('No active session found. Please sign in again.');
        return;
      }

      const { data, error } = await supabase.functions.invoke('auth-me', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });
      
      if (error) {
        const errorMessage = error.message || String(error);
        console.error('AUTH_CONTEXT_FETCH_FAILED', { error: errorMessage });
        
        // Check for rate limiting
        if (errorMessage.includes('429') || errorMessage.toLowerCase().includes('rate')) {
          rateLimitedUntilRef.current = Date.now() + RATE_LIMIT_COOLDOWN_MS;
          setContextError('Too many requests. Please wait a moment.');
          return;
        }
        
        // Check for auth errors - auto-recover
        if (errorMessage.includes('401') || 
            errorMessage.includes('403') || 
            errorMessage.includes('invalid') ||
            errorMessage.includes('JWT')) {
          const newCount = authFailureCount + 1;
          setAuthFailureCount(newCount);
          
          if (newCount >= MAX_AUTH_FAILURES) {
            console.warn('AUTH_AUTO_RECOVERY', { reason: 'repeated_failures', count: newCount });
            clearAuthStorageOnSignOut();
            await supabase.auth.signOut();
            setAuthContext(null);
            setUser(null);
            setSession(null);
            setContextError('Session expired. Please sign in again.');
            return;
          }
        }
        
        setContextError('Failed to load user context');
        return;
      }
      
      if (data) {
        const ctx = data as ExtendedAuthContext;
        setAuthContext(ctx);
        setAuthFailureCount(0);

        // Update shared auth state for API clients to read immediately.
        // This bridges the gap between "auth-me returned a business" and
        // "JWT metadata has been refreshed with the persisted value".
        setActiveBusinessId(ctx.activeBusinessId);

        // If auth-me returned an activeBusinessId that wasn't in the JWT,
        // the backend has now persisted it. Refresh the session so the JWT
        // picks up the new user_metadata.active_business_id for future requests.
        const currentJwtBusinessId = session.user?.user_metadata?.active_business_id ?? null;
        if (ctx.activeBusinessId && ctx.activeBusinessId !== currentJwtBusinessId) {
          supabase.auth.refreshSession().then(({ data: refreshData }) => {
            if (refreshData?.session) {
              setSession(refreshData.session);
              setUser(refreshData.user ?? null);
            }
          }).catch(() => {
            // Non-critical: shared authState already has the correct value
          });
        }

        // Recovery flow: If user has pending invite but no token in sessionStorage,
        // check user_metadata for pending_invite_token (cross-browser recovery)
        if (ctx.hasPendingInvite && typeof window !== 'undefined') {
          const storedToken = sessionStorage.getItem('pendingInviteToken');
          if (!storedToken) {
            // Check user_metadata for pending_invite_token
            const { data: { user: currentUser } } = await supabase.auth.getUser();
            const metadataToken = currentUser?.user_metadata?.pending_invite_token;
            if (metadataToken) {
              sessionStorage.setItem('pendingInviteToken', metadataToken);
              if (process.env.NODE_ENV === 'development') {
                console.info('AUTH_PENDING_INVITE_TOKEN_RECOVERED', { 
                  source: 'user_metadata',
                  hasToken: !!metadataToken 
                });
              }
            }
          }
        }
        
        // Mark initial load complete
        if (isInitialLoadRef.current) {
          isInitialLoadRef.current = false;
        }
        if (process.env.NODE_ENV === 'development') {
          console.info('AUTH_CONTEXT_LOADED', { 
            userId: ctx.userId,
            activeBusinessId: ctx.activeBusinessId,
            businessCount: ctx.businessMemberships?.length ?? 0,
            hasPendingInvite: ctx.hasPendingInvite
          });
        }
      }
    } catch (err) {
      const errorMessage = String(err);
      console.error('AUTH_CONTEXT_ERROR', { error: errorMessage });
      
      // Rate limit detection
      if (errorMessage.includes('429') || errorMessage.includes('Too Many')) {
        rateLimitedUntilRef.current = Date.now() + RATE_LIMIT_COOLDOWN_MS;
        setContextError('Too many requests. Please wait a moment.');
        return;
      }
      
      setContextError('Failed to load authentication context');
      // Clear loading state on error
      setContextLoading(false);
      if (isInitialLoadRef.current) {
        isInitialLoadRef.current = false;
      }
    } finally {
      isFetchingRef.current = false;
      // Always clear loading state in finally
      setContextLoading(false);
    }
  }, [authFailureCount]);

  // Switch active business
  const switchBusiness = useCallback(async (businessId: string) => {
    try {
      const { error } = await supabase.functions.invoke('set-active-business', {
        body: { business_id: businessId },
      });

      if (error) {
        console.error('SWITCH_BUSINESS_FAILED', { error: error.message });
        return false;
      }

      // Immediately clear org-scoped stores to prevent stale data flash
      useBatchStore.getState().clearForOrgSwitch();
      useConciliationStore.getState().clearForOrgSwitch();

      // Force-refresh the Supabase session to pick up updated JWT claims
      // (app_metadata.org_id, user_metadata.active_business_id).
      // Without this, the browser JWT carries stale claims until auto-refresh.
      // Apply the refreshed session/user to local state immediately so downstream
      // code (including fetchAuthContext) uses fresh claims in the same cycle.
      const { data: refreshData } = await supabase.auth.refreshSession();
      if (refreshData?.session) {
        setSession(refreshData.session);
        setUser(refreshData.user ?? null);
      }

      // Refresh auth context to get updated active business
      lastFetchTimeRef.current = 0; // Reset to allow immediate fetch
      await fetchAuthContext();
      return true;
    } catch (err) {
      console.error('SWITCH_BUSINESS_ERROR', { error: String(err) });
      return false;
    }
  }, [fetchAuthContext]);

  // Select active organization (used by Firm Selection page)
  const selectOrganization = useCallback(async (orgId: string) => {
    try {
      const { error } = await supabase.functions.invoke('set-active-organization', {
        body: { organization_id: orgId },
      });

      if (error) {
        console.error('SELECT_ORG_FAILED', { error: error.message });
        return false;
      }

      // Immediately clear org-scoped stores to prevent stale data flash
      useBatchStore.getState().clearForOrgSwitch();
      useConciliationStore.getState().clearForOrgSwitch();

      // Force-refresh the Supabase session to pick up updated app_metadata.org_id.
      // Without this, the browser JWT carries the old org claim and any immediate
      // PostgREST/RLS-backed queries run under the previous org.
      // Apply the refreshed session/user to local state immediately.
      const { data: refreshData } = await supabase.auth.refreshSession();
      if (refreshData?.session) {
        setSession(refreshData.session);
        setUser(refreshData.user ?? null);
      }

      // Refresh auth context to get updated org + business
      lastFetchTimeRef.current = 0;
      await fetchAuthContext();
      return true;
    } catch (err) {
      console.error('SELECT_ORG_ERROR', { error: String(err) });
      return false;
    }
  }, [fetchAuthContext]);

  // Refresh auth context
  const refreshAuth = useCallback(async () => {
    lastFetchTimeRef.current = 0; // Reset to allow immediate fetch
    await fetchAuthContext();
  }, [fetchAuthContext]);

  const signOut = useCallback(async () => {
    clearAuthStorageOnSignOut();
    // Clear org-scoped Zustand stores to prevent data leakage
    useBatchStore.getState().clearForOrgSwitch();
    useConciliationStore.getState().clearForOrgSwitch();
    resetActiveBusinessId();
    await supabase.auth.signOut();
    setAuthContext(null);
    setUser(null);
    setSession(null);
    setContextError(null);
    setAuthFailureCount(0);
  }, []);

  // Single Supabase auth listener for the entire app
  useEffect(() => {
    // Prevent double initialization in StrictMode
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        if (process.env.NODE_ENV === 'development') {
          console.info('AUTH_STATE_CHANGE', { event, hasSession: !!newSession });
        }
        
        // Extract session ID for comparison
        const newSessionId = newSession?.access_token?.substring(0, 32) ?? null;
        
        // Skip TOKEN_REFRESHED events - they don't need full UI refresh
        if (event === 'TOKEN_REFRESHED') {
          if (process.env.NODE_ENV === 'development') {
            console.info('AUTH_TOKEN_REFRESHED', { 
              sessionChanged: newSessionId !== prevSessionIdRef.current,
              isPageVisible: isPageVisibleRef.current 
            });
          }
          
          // Skip if page is hidden (tab is in background)
          if (!isPageVisibleRef.current) {
            if (process.env.NODE_ENV === 'development') {
              console.info('AUTH_TOKEN_REFRESH_SKIPPED', { reason: 'page_hidden' });
            }
            return;
          }
          
          // Skip if session is identical - no need to update state
          if (newSessionId === prevSessionIdRef.current) {
            if (process.env.NODE_ENV === 'development') {
              console.info('AUTH_TOKEN_REFRESH_SKIPPED', { reason: 'session_unchanged' });
            }
            return;
          }
          
          // Session is different, update but DON'T trigger full loading state
          prevSessionIdRef.current = newSessionId;
          setSession(newSession);
          setUser(newSession?.user ?? null);
          setLoading(false);
          return;
        }
        
        // Track session change for future comparisons
        prevSessionIdRef.current = newSessionId;
        
        // Skip if we're in the middle of a fetch
        if (isFetchingRef.current) {
          if (process.env.NODE_ENV === 'development') {
            console.info('AUTH_STATE_CHANGE_SKIPPED', { reason: 'fetch_in_progress' });
          }
          // Still update session/user state
          setSession(newSession);
          setUser(newSession?.user ?? null);
          setLoading(false);
          return;
        }
        
        setSession(newSession);
        setUser(newSession?.user ?? null);
        
        if (newSession?.user) {
          // Clear any pending debounce
          if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
          }
          
          // Debounce fetch to coalesce rapid events
          debounceTimeoutRef.current = setTimeout(() => {
            fetchAuthContext();
          }, AUTH_DEBOUNCE_MS);
        } else {
          // Signed out - clear context
          resetActiveBusinessId();
          setAuthContext(null);
          setContextError(null);
          setContextLoading(false);
        }
        
        setLoading(false);
      }
    );

    // Check for existing session on mount
    supabase.auth.getSession().then(({ data: { session: existingSession } }) => {
      setSession(existingSession);
      setUser(existingSession?.user ?? null);
      prevSessionIdRef.current = existingSession?.access_token?.substring(0, 32) ?? null;
      
      if (existingSession?.user) {
        fetchAuthContext();
      } else {
        isInitialLoadRef.current = false;
        setContextLoading(false);
      }
      
      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [fetchAuthContext]);

  // Derive values
  const activeBusiness = authContext?.activeBusiness ?? null;
  const organizations = useMemo(() => authContext?.organizations ?? [], [authContext]);

  // Resolve the active org deterministically:
  //   1. activeBusiness's org (strongest signal — user is working in this business)
  //   2. Backend-provided activeOrgId from JWT (set by set-active-organization)
  //   3. session app_metadata.org_id (fallback for older backend responses)
  //   4. Only default to organizations[0] for single-org users
  // For multi-org users without an explicit active org, activeOrg stays null
  // to prevent silently binding to the wrong firm.
  const resolvedActiveOrgId =
    activeBusiness?.orgId
    ?? authContext?.activeOrgId
    ?? session?.user?.app_metadata?.org_id
    ?? (organizations.length === 1 ? organizations[0]?.orgId : null)
    ?? null;

  const activeOrg = resolvedActiveOrgId
    ? organizations.find(o => o.orgId === resolvedActiveOrgId) ?? null
    : null;

  const contextReady = !loading && !contextLoading;
  const initializing = loading || (!!session && authContext === null && !contextError);

  // Use backend signal for deterministic firm selection (safe fallback for older responses)
  const needsFirmSelection =
    !!session &&
    authContext !== null &&
    (authContext.requiresFirmSelection ?? false);

  const businessMemberships = useMemo(() => authContext?.businessMemberships ?? [], [authContext]);
  const isSuperAdmin = authContext?.platformRole === 'super_admin';
  // Scoped to active org only — ownership in another firm must NOT elevate
  // privileges in the current session's firm.
  const isOrgOwner = activeOrg?.role === 'owner';
  const isOrgAdmin = activeOrg?.role === 'owner' || activeOrg?.role === 'admin';

  const value: AuthContextValue = useMemo(() => ({
    user,
    session,
    loading: loading || contextLoading,
    initializing,
    contextReady,
    contextError,
    signOut,
    isAuthenticated: !!session,

    platformRole: authContext?.platformRole ?? null,
    organizations,
    businessMemberships,
    activeBusinessId: authContext?.activeBusinessId ?? null,
    activeBusiness,
    clientType: authContext?.clientType ?? null,
    hasPendingInvite: authContext?.hasPendingInvite ?? false,
    singleBusinessMode: authContext?.singleBusinessMode ?? false,

    switchBusiness,
    selectOrganization,
    refreshAuth,

    isSuperAdmin,
    isOrgOwner,
    isOrgAdmin,
    needsFirmSelection,
    activeOrg,

    orgId: activeBusiness?.orgId ?? activeOrg?.orgId ?? null,
    orgName: activeOrg?.orgName ?? null,
    orgCode: activeOrg?.orgCode ?? null,
    role: activeBusiness?.role ?? null,
    fullName: authContext?.fullName ?? null,
    displayName: authContext?.displayName ?? authContext?.fullName ?? null,
  }), [
    user, session, loading, contextLoading, initializing, contextReady, contextError,
    signOut, authContext, organizations, businessMemberships, activeBusiness,
    switchBusiness, selectOrganization, refreshAuth,
    isSuperAdmin, isOrgOwner, isOrgAdmin, needsFirmSelection, activeOrg,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// =============================================================================
// HOOK
// =============================================================================

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
}
